import { useRef, useState } from "react";
import {
	Mosaic,
	type MosaicNode,
	type MosaicPath,
	MosaicWindow,
} from "react-mosaic-component";
import "react-mosaic-component/react-mosaic-component.css";
import { useMosaic } from "@/context/mosaic/MosaicContext";
import { useTitleMap } from "@/context/mosaic/TitleMosaicProvider";
import { useTabs } from "@/context/tabs/TabsContext";
import type { WidgetState } from "@/context/tabs/types";
import { useAuthService } from "@/hooks/useAuthService";
import { isEnabledAdvancedChart } from "@/lib/utils/features";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { OfferToUpgrade } from "@/shared/components/OfferToUpgrade";
import {
	DEFAULT_TICKER,
	WIDGETS_WITH_TICKERS_LIST,
} from "@/shared/constants/common";
import { mosaicSubToolbarClassNames } from "@/shared/constants/customStyleSets";
import { EVENTS, globalEventEmitter } from "@/shared/eventEmiters/consts";
import { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import type { ViewId } from "@/shared/types/common";
import { Button } from "../ui/button";
import { type ChartState, ChartTypes, Datasets } from "../widgets/Chart/types";
import { CheckboxPopover } from "../widgets/Chart/ui/ChartDropdown/CheckboxPopover";
import { isTickerFilter } from "../widgets/News/lib/helpers/isTickerFilter";
import { Ticker } from "../widgets/Ticker/Ticker";
import { mosaicIncludesNode, replaceMosaicNode } from "./helpers";
import { useRefreshLinkedTilesOnClick } from "./hooks/useRefreshLinkedTilesOnClick";
import { useUpdateTickerForLinkedWidgets } from "./hooks/useUpdateTickerForLinkedWidgets";
import { ToolbarControls } from "./toolbarControls/ToolbarControls";
import { TileTitle } from "./ui/TileTitle";

export type MosaicState = {
	[id: string]: {
		color?: string | null;
		ticker?: string;
	};
};

export const MosaicComponent = () => {
	const {
		setTabLayout,
		getTabLayout,
		getTabWidgetsState,
		setTabWidgetsState,
		getWidgetState,
		setWidgetState,
		getActiveTab,
	} = useTabs();
	const { clickInfo, generateUniqueId, counter, setCounter } = useMosaic();

	const newLayout = getTabLayout();
	const widgetsState = getTabWidgetsState();
	const activeTab = getActiveTab();
	const authService = useAuthService();

	const { titleMap } = useTitleMap();

	const [widgetBeforeNav, setWidgetBeforeNav] = useState<ViewId>();
	const navigationId = "Navigation";

	const isResizingRef = useRef<boolean>(false);

	const isEnabledAdvChart = isEnabledAdvancedChart();

	const handleLayoutChange = (newState: MosaicNode<ViewId> | null) => {
		setTabLayout(newState);
	};

	const handleTickerChange = (id: string, value: string) => {
		const color = widgetsState[id]?.color;
		const updatedState = { ...widgetsState };

		const isListWidget = WIDGETS_WITH_TICKERS_LIST.some((el: string) =>
			id?.startsWith(el),
		);

		for (const key in updatedState) {
			const isKeyListWidget = WIDGETS_WITH_TICKERS_LIST.some((el: string) =>
				key.startsWith(el),
			);
			const isNewsWidget = key.startsWith("News");

			if (isListWidget) {
				if (
					key === id ||
					(!isKeyListWidget && updatedState[key]?.color === color)
				) {
					updatedState[key] = {
						...updatedState[key],
						ticker: value,
					};
				}
			} else {
				if (!isKeyListWidget && updatedState[key]?.color === color) {
					updatedState[key] = {
						...updatedState[key],
						ticker: isTickerFilter(value) ? "" : value,
						...(isNewsWidget && {
							tickerFilter: isTickerFilter(value) ? value : undefined,
						}),
					};
				}
			}
		}

		if (isListWidget) {
			updatedState[id] = {
				...updatedState[id],
				ticker: value,
			};
		}

		setTabWidgetsState(updatedState);
	};

	const assignGroup = (id: string, color: string | null) => {
		const currentTicker = widgetsState[id]?.ticker || "";
		const updatedState = { ...widgetsState };

		const firstTicker =
			Object.values(updatedState).find((item) => item?.color === color)
				?.ticker || currentTicker;

		for (const key in updatedState) {
			if (
				updatedState[key]?.color === color &&
				!WIDGETS_WITH_TICKERS_LIST.some((el) => key.startsWith(el))
			) {
				updatedState[key] = {
					...updatedState[key],
					ticker: firstTicker,
				};
			}
		}

		updatedState[id] = {
			...updatedState[id],
			color: color as string | undefined,
			ticker: firstTicker,
		};

		setTabWidgetsState(updatedState);
	};

	const handleOpenNavigation = (id: string) => {
		let updatedMosaicState = newLayout;

		if (mosaicIncludesNode(newLayout!, navigationId)) {
			updatedMosaicState = replaceMosaicNode(
				newLayout!,
				navigationId,
				widgetBeforeNav!,
			);
			setTabLayout(updatedMosaicState);
		}

		updatedMosaicState = replaceMosaicNode(
			updatedMosaicState!,
			id,
			navigationId,
		);
		setTabLayout(updatedMosaicState);

		const updatedState = { ...widgetsState };
		updatedState[navigationId] = updatedState[id];

		setTabWidgetsState(updatedState);
		setWidgetBeforeNav(id as ViewId);
	};

	const handleOpenWidgetInNavigation = (id: ViewId) => {
		const uniqueViewId = generateUniqueId(id);
		const updatedState = replaceMosaicNode(
			newLayout!,
			navigationId,
			uniqueViewId,
		);

		setTabLayout(updatedState);
		setCounter(counter + 1);

		const updatedWidgetsState = { ...widgetsState };
		updatedWidgetsState[uniqueViewId] = updatedWidgetsState[navigationId];
		const { [widgetBeforeNav!]: _, ...newState } = updatedWidgetsState;

		setTabWidgetsState(newState);
	};

	useUpdateTickerForLinkedWidgets(widgetsState);
	useRefreshLinkedTilesOnClick(clickInfo, handleTickerChange); // run when user clicks on the Screener or Monitor table row

	const renderTile = (id: ViewId, path: MosaicPath) => {
		if (typeof id !== "string") {
			ErrorHandler.logError(`Invalid id", ${id}`);

			return <div />;
		}

		const baseId = id.split("-")[0] as ViewId;
		const renderContent = titleMap[baseId];

		const eventEmitter = new EventEmitter();

		const prevState = getWidgetState(id);

		const handleStateChange = (
			state: WidgetState | undefined,
			shouldRerender = true,
		) => {
			const nextState: WidgetState | undefined = {
				...prevState,
				...state,
			};

			setWidgetState(id as ViewId, nextState, shouldRerender);
		};

		const isAdvancedChart =
			isEnabledAdvChart &&
			id.includes("Chart") &&
			((prevState as ChartState)?.type ?? ChartTypes.ADVANCED) ===
				ChartTypes.ADVANCED;

		const isMonitor = id.includes("Monitor");

		return (
			<MosaicWindow<ViewId>
				data-testid="mosaic-window"
				path={path}
				title={
					(
						<TileTitle
							baseId={baseId}
							id={id}
							ticker={widgetsState[id]?.ticker || ""}
							eventEmitter={eventEmitter}
							handleTickerChange={handleTickerChange}
							handleOpenNavigation={handleOpenNavigation}
							state={widgetsState[id] as Record<string, unknown>}
							onStateChange={handleStateChange}
							activeTabId={activeTab}
						/>
					) as unknown as string
				}
				createNode={() => baseId}
				toolbarControls={
					<ToolbarControls
						path={path}
						id={id}
						onColorChange={(color) => assignGroup(id, color)}
						passedColor={widgetsState[id]?.color}
						eventEmitter={eventEmitter}
					/>
				}
				draggable={true} // temporary!!! previous value // draggable={isMosaicTileDraggable({ tabs, activeTab })}
			>
				<div className="absolute top-0 bottom-0 left-0 right-0" id={id}>
					{isAdvancedChart && (
						<div className="text-xs h-5 w-full flex items-center bg-(--dark-background) pl-3">
							<Button
								variant="dropdown"
								className="font-bold text-sm h-full"
								aria-label="Open Technical Analysis indicators"
								onClick={() => {
									handleStateChange({ openIndicatorsDialog: true });
								}}
							>
								Studies
							</Button>

							<div className="flex pl-4 !w-[70px]">
								<CheckboxPopover
									arrayType={Object.values(Datasets)}
									checkedValues={
										((prevState as ChartState)?.datasets as Datasets[]) ?? []
									}
									buttonText="Apply"
									displayText="Datasets"
									stateKey="datasets"
									applyStateKey="applyDatasets"
									onStateChange={handleStateChange}
								/>
							</div>
						</div>
					)}

					{isMonitor && (
						<div className={mosaicSubToolbarClassNames}>
							<Ticker
								ticker={widgetsState[id]?.ticker || DEFAULT_TICKER}
								onChange={(value) => handleTickerChange(id, value)}
							/>
						</div>
					)}

					{renderContent && authService.allowedWidgets.includes(baseId) ? ( // Check if the widget is allowed
						renderContent({
							state: widgetsState[id] || {},
							eventEmitter,
							tileId: id,
							activeTabId: activeTab,
							onStateChange: handleStateChange,
							updateState: handleOpenWidgetInNavigation,
						})
					) : (
						<div className="flex items-center justify-center h-full w-full">
							<OfferToUpgrade
								offer="Upgrade to unlock"
								className="flex items-center"
							/>
						</div>
					)}
				</div>
			</MosaicWindow>
		);
	};

	return (
		<div
			className="mosaic"
			onMouseDown={() => {
				if (!isResizingRef.current) {
					isResizingRef.current = true;
					globalEventEmitter.emit(EVENTS.RESIZING, true);
				}
			}}
			onMouseUp={() => {
				globalEventEmitter.emit(EVENTS.RESIZING, false);
				isResizingRef.current = false;
			}}
		>
			{newLayout ? (
				<Mosaic<ViewId>
					renderTile={renderTile}
					value={newLayout}
					onChange={(newState) => {
						handleLayoutChange(newState);
					}}
					resize={{ minimumPaneSizePercentage: 20 }}
				/>
			) : (
				<div className="empty-state m-auto">No Windows Present</div>
			)}
		</div>
	);
};
